import styled from '@emotion/styled'
import { Link, LinkProps } from 'react-router-dom'

import { fonts, colors, metrics, zIndex } from 'component-library/__styling/settings'
import { calcRem, breakpoint, breakpointMax } from 'component-library/__styling/style-functions'
import { easing } from 'component-library/__styling/settings/easing'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'
import {
    IconChevronRight,
    Props as ChevronProps,
} from 'component-library/icons/IconChevronRight/IconChevronRight'

const navBreakpoint = 'xs'
interface CommonProps {
    isOpen: boolean
}

export const StyledSubNav = styled('nav')({
    position: 'absolute',
    top: '100%',
    left: 0,
    zIndex: zIndex.sevennews.subNavDropDown,
    flexDirection: 'column',
    overflow: 'hidden',
    width: '100%',
    transition: `max-height ${easing.easeOut.fast}`,

    display: 'flex',
    maxHeight: calcRem(1450),
    fontFamily: fonts.thewest.sansSerif,
    boxShadow: `0 2px 9px ${colors.thewest.greyHippo}`,

    '& span': {
        width: '100%',
    },
})

export const StyledSubNavHeader = styled('h2')({
    fontSize: calcRem(17),
    display: 'flex',
    overflow: 'hidden',
    margin: 0,
    padding: calcRem(metrics.globalPadding / 2, metrics.globalPadding / 1.3),
    width: '100%',
    textTransform: 'uppercase',
    fontWeight: 600,
    alignItems: 'center',
    alignSelf: 'flex-start',

    [breakpoint(navBreakpoint)]: {
        padding: calcRem(21),
        paddingRight: calcRem(21 / 2), // avoid too much padding which causes chevron dropping below in FF
        width: calcRem(190),
        borderBottom: 0,
        flexShrink: 0,
    },
})

export const StyledSubNavHeaderLink = styled(Link)<LinkProps>(({ theme }) => ({
    flex: '3 0 auto',
    color: colors.black,
    textDecoration: 'none',
    transition: `color ${easing.easeOut.fast}`,

    '&:focus': {
        outline: 'none',
    },
    '&:hover, &:focus': {
        color: theme.sectionValues.primaryColor,
        textDecoration: 'underline',
    },
}))

export const StyledSubNavHeaderChevron = styled(IconChevronRight)<ChevronProps>(({ theme }) => ({
    marginLeft: calcRem(metrics.globalMargin / 2),
    width: calcRem(9.8),
    height: calcRem(14),
    transform: 'translateY(1px)',
    fill: theme.sectionValues.primaryColor,
}))

export const StyledSubNavList = styled('li')({
    margin: 0,
    padding: 0,
    backgroundColor: colors.white,
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',

    [breakpoint(navBreakpoint)]: {
        flexDirection: 'row',
    },
    '&:nth-child(odd)': {
        background: colors.thewest.greyMouse,
    },
})

export const StyledSubNavExpandToggle = styled('button')<CommonProps>(
    ({ theme }) => ({
        display: 'none',

        [breakpointMax('xs')]: {
            display: 'inline-block',
            flex: '0 0 auto',
            padding: calcRem(8),
            minWidth: calcRem(82),
            border: 0,
            background: 'none',
            textAlign: 'right',
            transition: `background ${easing.easeOut.fast}`,
            alignSelf: 'flex-end',
        },

        '&::after': {
            display: 'inline-block',
            marginLeft: calcRem(metrics.globalMargin / 2),
            width: 0,
            height: 0,
            borderWidth: calcRem(10.4, 6, 0),
            borderStyle: 'solid',
            borderColor: `${colors.black} transparent transparent`,
            content: `' '`,
            transition: `transform ${easing.easeOut.fast}`,
            backfaceVisibility: 'hidden',
        },
        '&:hover': {
            background: theme.sectionValues.primaryColor,
            color: colors.white,

            '&::after': {
                borderColor: `${colors.white} transparent transparent`,
            },
        },
        '&:focus': {
            outline: 'none',
            background: colors.thewest.greyDolphin,

            color: colors.black,

            '&::after': {
                borderColor: `${colors.thewest.westblue} transparent transparent`,
            },
        },
    }),
    ({ isOpen }) =>
        conditionalStyles(isOpen === true, {
            '&::after': {
                transform: 'rotate(-180deg)',
            },
        })
)

export const StyledSubNavLinks = styled('ul')<CommonProps>(
    {
        fontSize: calcRem(17),
        display: 'flex',
        margin: 0,
        padding: calcRem(metrics.globalMargin / 2),
        width: '100%',
        listStyle: 'none',
        fontWeight: 400,
        alignItems: 'center',
        flexFlow: 'row wrap',
        justifyContent: 'flex-start',

        [breakpointMax(navBreakpoint)]: {
            display: 'none',
            overflow: 'hidden',
            maxHeight: 0,
            transition: `max-height ${easing.easeOut.fast}, padding ${easing.easeOut.fast}`,
        },
    },
    props =>
        conditionalStyles(props.isOpen === true, {
            [breakpointMax(navBreakpoint)]: {
                display: 'flex',
                padding: calcRem(8, 0),
                maxHeight: 500,
            },

            '&::before': {
                [breakpointMax(navBreakpoint)]: {
                    position: 'relative',
                    top: calcRem(-8),
                    display: 'block',
                    margin: '0 auto',
                    width: 'calc(100% - 30px)',
                    height: 'calc-rem(1px)',
                    background: colors.thewest.greyQuokka,
                    content: `''`,
                },
            },
        })
)

export const StyledSubNavItem = styled('li')({
    width: '50%',

    [breakpoint(navBreakpoint)]: {
        display: 'flex',
        width: 'auto',
    },
})

export const StyledSubNavLink = styled(Link)<LinkProps>(({ theme }) => ({
    fontSize: calcRem(17),
    display: 'inline-block',
    padding: calcRem(8, 16),
    color: colors.black,
    textDecoration: 'none',
    fontWeight: 400,
    transition: `color ${easing.easeOut.fast}`,

    [breakpoint('sm')]: {
        fontSize: calcRem(15),
        padding: calcRem(8, 16),
    },

    '&:focus': {
        outline: 'none',
    },
    '&:hover, &:focus, &:active': {
        color: theme.sectionValues.primaryColor,
        textDecoration: 'underline',
    },
}))

export const StyledSubNavTitle = styled('h2')(({ theme }) => ({
    fontSize: calcRem(18),
    margin: 0,
    padding: `${calcRem(metrics.globalPadding * 0.8)}`,
    color: colors.white,
    textTransform: 'uppercase',
    fontWeight: 600,
    background: theme.sectionValues.primaryColor,
}))
